import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, ExternalLink, Lock, FileText, Building2, ArrowDownLeft, ArrowUpRight, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const POLICY_URL = 'https://info.destine.studio/document';

const tr = (t, key, fallback) => {
  const v = t(key);
  return !v || v === key ? fallback : v;
};

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    accepted: { label: 'Accepté', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
    rejected: { label: 'Refusé', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
  };
  const m = map[status] || { label: status, cls: 'bg-white/10 text-white/70 border-white/10' };
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
};

export default function MonetizationSection() {
  const { t } = useLanguage();
  const [eligibility, setEligibility] = useState(null);
  const [iban, setIban] = useState({ iban: '', bic: '', holder_name: '' });
  const [savingIban, setSavingIban] = useState(false);
  const [requests, setRequests] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutTotals, setPayoutTotals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const [el, ib, rq, po] = await Promise.all([
          axios.get('/api/monetization/eligibility'),
          axios.get('/api/monetization/iban'),
          axios.get('/api/monetization/requests/outgoing'),
          axios.get('/api/monetization/payouts/mine').catch(() => ({ data: {} })),
        ]);
        if (!on) return;
        setEligibility(el.data);
        setIban({ iban: ib.data?.iban || '', bic: ib.data?.bic || '', holder_name: ib.data?.holder_name || '' });
        setRequests(rq.data?.requests || []);
        setPayouts(po.data?.payouts || []);
        setPayoutTotals(po.data?.totals_by_currency || {});
      } catch (_e) { /* ignore */ }
      finally { if (on) setLoading(false); }
    })();
    return () => { on = false; };
  }, []);

  const saveIban = async () => {
    if (!iban.iban || iban.iban.replace(/\s/g, '').length < 15) {
      toast.error('IBAN invalide');
      return;
    }
    setSavingIban(true);
    try {
      await axios.post('/api/monetization/iban', iban);
      toast.success(tr(t, 'monetization.iban_saved', 'IBAN enregistré'));
    } catch (_e) {
      toast.error('Échec sauvegarde');
    } finally {
      setSavingIban(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400" data-testid="monetization-section">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const isEligible = !!eligibility?.is_eligible;
  const followers = eligibility?.followers ?? 0;
  const threshold = eligibility?.threshold ?? 5000;
  const progress = Math.min(100, Math.round((followers / threshold) * 100));

  return (
    <div className="space-y-4" data-testid="monetization-section">
      {/* Eligibility card */}
      <div className="p-5 rounded-2xl bg-gradient-to-br from-magenta-500/10 to-violet-500/10 border border-magenta-500/30">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold flex items-center gap-2">
              {tr(t, 'monetization.eligibility', 'Monétisation Destine')} {isEligible ? null : <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </h3>
            <p className="text-sm text-gray-400">
              {isEligible
                ? tr(t, 'monetization.unlocked', 'Tu peux ajouter un sponsor sur tes Gim Toli, podcasts et films courts.')
                : tr(t, 'monetization.locked', `Débloque la monétisation à ${threshold.toLocaleString('fr-FR')} abonnés.`).replace('{threshold}', threshold.toLocaleString('fr-FR'))}
            </p>
          </div>
        </div>
        <div className="text-xs text-gray-400 mb-1.5 flex justify-between">
          <span>{followers.toLocaleString('fr-FR')} {tr(t, 'monetization.followersOf', 'abonnés')}</span>
          <span>{threshold.toLocaleString('fr-FR')}</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-magenta-500 to-violet-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <a
          href={POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-magenta-300 hover:text-magenta-200"
          data-testid="monetization-policy-link"
        >
          <FileText className="w-4 h-4" /> {tr(t, 'monetization.policy', 'Politique de monétisation')} <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* IBAN card */}
      <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-cyan-400" /> {tr(t, 'monetization.iban', 'Coordonnées bancaires (IBAN)')}</h3>
        <p className="text-xs text-gray-400 mb-3">{tr(t, 'monetization.ibanHelp', 'Visible uniquement par les comptes PRO ayant accepté un sponsoring.')}</p>
        <div className="space-y-2">
          <input
            type="text"
            placeholder="IBAN"
            value={iban.iban}
            onChange={(e) => setIban({ ...iban, iban: e.target.value.toUpperCase() })}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg font-mono text-sm focus:outline-none focus:border-magenta-500"
            data-testid="iban-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="BIC"
              value={iban.bic}
              onChange={(e) => setIban({ ...iban, bic: e.target.value.toUpperCase() })}
              className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg font-mono text-sm focus:outline-none focus:border-magenta-500"
              data-testid="bic-input"
            />
            <input
              type="text"
              placeholder="Titulaire"
              value={iban.holder_name}
              onChange={(e) => setIban({ ...iban, holder_name: e.target.value })}
              className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-magenta-500"
              data-testid="holder-input"
            />
          </div>
          <button
            type="button"
            onClick={saveIban}
            disabled={savingIban}
            className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 font-semibold text-sm disabled:opacity-50"
            data-testid="iban-save-btn"
          >
            {savingIban ? '…' : tr(t, 'monetization.iban_save', 'Enregistrer')}
          </button>
        </div>
      </div>

      {/* Payouts received (Stripe in-app payments) */}
      <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30" data-testid="payouts-section">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" /> {tr(t, 'monetization.payouts', 'Paiements reçus')}
        </h3>
        {Object.keys(payoutTotals).length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {Object.entries(payoutTotals).map(([c, tot]) => (
              <div key={c} className="p-2.5 bg-black/20 rounded-xl border border-emerald-500/20" data-testid={`payout-total-${c}`}>
                <div className="text-[10px] uppercase text-emerald-300/70">{c}</div>
                <div className="text-lg font-bold tabular-nums text-emerald-200">{formatAmount(tot, c)}</div>
              </div>
            ))}
          </div>
        )}
        {payouts.length === 0 ? (
          <p className="text-sm text-gray-500">{tr(t, 'monetization.noPayouts', 'Aucun paiement reçu pour le moment.')}</p>
        ) : (
          <div className="space-y-1.5">
            {payouts.slice(0, 20).map((p) => (
              <div key={p.id} className="p-2.5 bg-white/5 rounded-lg border border-white/10 flex items-center justify-between gap-2 text-sm" data-testid={`payout-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.sponsor_name}</div>
                  <div className="text-xs text-gray-400">
                    {p.content_kind === 'gim_toli' ? 'Gim Toli' : p.content_kind === 'podcast' ? 'Podcast' : p.content_kind === 'video' ? 'Vidéo' : 'Film court'} · {new Date(p.paid_at || p.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div className="text-sm font-bold text-emerald-300 tabular-nums shrink-0">{formatAmount(p.amount, p.currency)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Outgoing requests */}
      <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-emerald-400" /> {tr(t, 'monetization.requests', 'Mes demandes de sponsoring')}</h3>
        {requests.length === 0 && <p className="text-sm text-gray-500">{tr(t, 'monetization.noRequests', 'Aucune demande envoyée pour le moment.')}</p>}
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="p-3 bg-white/5 border border-white/10 rounded-xl" data-testid={`outgoing-request-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.sponsor_name}</div>
                  <div className="text-xs text-gray-400">
                    {r.content_kind === 'gim_toli' ? 'Gim Toli' : r.content_kind === 'podcast' ? 'Podcast' : r.content_kind === 'video' ? 'Vidéo' : 'Film court'} · {formatAmount(r.amount, r.currency)}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>
              {r.status === 'accepted' && r.id && (
                <ClickCounter requestId={r.id} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClickCounter({ requestId }) {
  const [n, setN] = useState(null);
  useEffect(() => {
    let on = true;
    axios.get(`/api/monetization/clicks/${requestId}/count`).then((r) => { if (on) setN(r.data?.clicks || 0); }).catch(() => {});
    return () => { on = false; };
  }, [requestId]);
  return (
    <div className="mt-2 text-xs text-magenta-300 flex items-center gap-1">
      <ArrowDownLeft className="w-3 h-3" /> {n ?? '…'} clics sur le lien sponsor
    </div>
  );
}
